// GA4 tracking for the public team brand page /:slug.
//
// The page already sends many events from many components. Instead of editing
// each call site, this tracker watches every event through setPageTracking:
// it stamps each one with the page load's flow_id and visitor_kind, counts the
// visitor's actions, and sends one team_page_leave per page load.
//
// ⛔ Every event counts as an action unless it is listed in PASSIVE_EVENTS.
// When you add an impression, an automatic event, or a result event to the
// team page, add it here, or it will be counted as something the visitor did.
import { trackEvent, setPageTracking } from 'utils/analytics'
import { sendLeaveOnExit } from 'utils/findTeacherTracking'

export const LEAVE_EVENT = 'team_page_leave'
export const SECTION_DWELL_EVENT = 'section_dwell'

// Each section's time in view goes out once, when the visitor leaves. Sections
// seen for less than a second are noise, and five events cap the volume of one
// page load.
const MIN_DWELL_MS = 1000
const MAX_DWELL_EVENTS = 5
// A section is in view when this share of it, or this share of the viewport
// height, is inside the viewport. The second rule is for sections taller than
// three viewports, whose own ratio can never reach the first.
const IN_VIEW_SHARE = 0.3

export const PASSIVE_EVENTS = new Set([
  // Impressions and returns
  'section_view',
  'tab_revisit',
  'welcome_coupon_banner_impression',
  // Fires when the payment modal mounts
  'checkout_modal_open',
  // Results of an earlier action
  'ecpay_checkout_success',
  'ecpay_checkout_failed',
  'purchase',
  'checkout_success',
  'checkout_failed',
  'booking_completed',
  LEAVE_EVENT,
  // Sent on leave, once per section
  SECTION_DWELL_EVENT
])

// Reaching any of these means the visitor reached a conversion step.
// A signed-out visitor who sends one of LOGIN_WALL_EVENTS only reaches the
// login wall, so it is not a completion for that page load.
export const LOGIN_WALL_EVENTS = new Set(['purchase_initiated', 'trial_claim_initiated'])

export const COMPLETION_EVENTS = new Set([
  'purchase_initiated',
  'checkout_submitted',
  'trial_claim_initiated',
  'consultation_submitted',
  'booking_completed',
  'group_class_enroll_confirm'
])

// @param {IntersectionObserverEntry} entry
// @returns {boolean} whether the section counts as in view
export function isSectionInView(entry) {
  if (entry.intersectionRatio >= IN_VIEW_SHARE) return true
  const viewportHeight = entry.rootBounds?.height || (typeof window !== 'undefined' ? window.innerHeight : 0)
  return viewportHeight > 0 && entry.intersectionRect.height >= IN_VIEW_SHARE * viewportHeight
}

function randomId() {
  return Math.random().toString(36).slice(2, 10)
}

// @param {Object} options
// @param {Function} options.send defaults to trackEvent
// @param {Function} options.now defaults to Date.now
// @param {boolean} options.signedIn whether a viewer is signed in
export function createTeamPageTracker({ send = trackEvent, now = Date.now, signedIn = false } = {}) {
  const flowId = randomId()
  const pageStartedAt = now()
  const sectionsSeen = new Set()
  let lastSection = ''
  let actionsCount = 0
  let lastAction = 'none'
  let completed = false
  let left = false
  let pageVisible = true
  let nextOrder = 0
  let enterCount = 0
  // The section of the latest click that landed inside a [data-section] element.
  // A click outside any section (a dialog, say) does not replace it.
  let lastClickSection = ''
  // name -> { ms, order, inView, startedAt }. Time only runs while the section
  // is in view and the tab is visible.
  const sections = new Map()

  const stopTimer = (section) => {
    if (section.startedAt === null) return
    section.ms += now() - section.startedAt
    section.startedAt = null
  }
  const startTimer = (section) => {
    if (section.startedAt === null && section.inView && pageVisible) section.startedAt = now()
  }

  const summary = () => ({
    screen: 'team_page',
    seconds_on_page: Math.round((now() - pageStartedAt) / 1000),
    actions_count: actionsCount,
    last_action: lastAction,
    bounced: actionsCount === 0 && !completed,
    completed,
    section_name: lastSection,
    sections_seen: sectionsSeen.size
  })

  return {
    flowId,
    summary,
    // Called when a section enters or leaves the viewport.
    setSectionVisible(name, visible) {
      let section = sections.get(name)
      if (!section) {
        if (!visible) return
        section = { ms: 0, order: nextOrder += 1, inView: false, startedAt: null, enteredAt: 0 }
        sections.set(name, section)
      }
      if (visible && !section.inView) section.enteredAt = enterCount += 1
      section.inView = visible
      if (visible) startTimer(section)
      else stopTimer(section)
    },
    // Called for every click on the page with the section it landed in ('' for
    // none).
    recordClick(sectionName) {
      if (sectionName) lastClickSection = sectionName
    },
    // The section an action event belongs to: where the visitor last clicked,
    // else the section that came into view most recently, else none. Events
    // that are not visitor actions get none.
    deriveParams(name, params = {}) {
      if (PASSIVE_EVENTS.has(name) || params.resumed) return {}
      if (lastClickSection) return { section_name: lastClickSection }
      let latest = null
      sections.forEach((section, sectionName) => {
        if (section.inView && (!latest || section.enteredAt > latest.enteredAt)) latest = { ...section, name: sectionName }
      })
      return latest ? { section_name: latest.name } : {}
    },
    // Called when the tab is hidden or shown again.
    setPageVisible(visible) {
      if (visible === pageVisible) return
      pageVisible = visible
      sections.forEach(visible ? startTimer : stopTimer)
    },
    // Called for every event the page sends. params.resumed marks an event the
    // page sent by itself after login, to finish what the visitor clicked
    // before it: it can complete the page load but is not an action.
    record(name, params = {}) {
      if (name === 'section_view' && params.section_name) {
        sectionsSeen.add(params.section_name)
        lastSection = params.section_name
      }
      const blockedByLogin = !signedIn && LOGIN_WALL_EVENTS.has(name)
      if (COMPLETION_EVENTS.has(name) && !blockedByLogin) completed = true
      if (PASSIVE_EVENTS.has(name) || params.resumed) return
      actionsCount += 1
      lastAction = name
    },
    // sendLeaveOnExit calls this on every hide. team_page_leave goes out once
    // per page load. Dwell time goes out on every hide and then restarts from
    // zero, so time after the visitor comes back to the tab still counts.
    leave() {
      if (!left) {
        left = true
        try {
          send(LEAVE_EVENT, {
            ...summary(),
            flow_id: flowId,
            // Survives the page unloading; a plain request would be cancelled.
            transport_type: 'beacon'
          })
        } catch (error) {
          // Analytics must never break the page.
          console.error('[Analytics] team_page_leave failed:', error)
        }
      }
      sections.forEach(stopTimer)
      const dwelled = [...sections.entries()]
        .filter(([, section]) => section.ms >= MIN_DWELL_MS)
        .sort(([, a], [, b]) => b.ms - a.ms || a.order - b.order)
        .slice(0, MAX_DWELL_EVENTS)
      dwelled.forEach(([name, section]) => {
        const ms = section.ms
        section.ms = 0
        try {
          send(SECTION_DWELL_EVENT, {
            screen: 'team_page',
            section_name: name,
            dwell_seconds: Math.round(ms / 1000),
            flow_id: flowId,
            transport_type: 'beacon'
          })
        } catch (error) {
          console.error('[Analytics] section_dwell failed:', error)
        }
      })
    }
  }
}

// Who is looking, decided once from the page data. First match wins: a team
// teacher can also be a student, and members browsing their own page are
// internal traffic that reports must be able to drop as one group.
// team_student means the viewer already has a relationship with the team (an
// invite link, a trial claim, or a course order), not that they have paid.
//
// @param {Object} initialData the parsed data-team-data payload
// @returns {string} team_member, team_student, signed_in or anonymous
export function visitorKindFor(initialData) {
  const user = initialData?.currentUser
  if (initialData?.isOwner || user?.isTeamMember) return 'team_member'
  if (user?.isTeamStudent) return 'team_student'
  if (user) return 'signed_in'
  return 'anonymous'
}

let activeTracker = null

// The tracker of this page load, for components that report which section is on
// screen. Null before startTeamPageTracking runs (and in tests that render a
// component alone), so callers must cope with that.
export const getTeamPageTracker = () => activeTracker

// Starts tracking for this page load. Call it from the page entry before
// rendering: some events fire in mount effects (ecpay_checkout_success, for
// one), and child effects run before the parent's, so a tracker created inside
// a component would miss them.
//
// @param {Object} initialData the parsed data-team-data payload
// @returns {Object} the tracker, with stop() to remove the leave listeners
export function startTeamPageTracking(initialData = {}) {
  const tracker = createTeamPageTracker({ signedIn: Boolean(initialData?.currentUser) })
  setPageTracking({
    params: { flow_id: tracker.flowId, visitor_kind: visitorKindFor(initialData) },
    onEvent: tracker.record,
    deriveParams: tracker.deriveParams
  })
  // Capture phase and passive: it runs before the page's handlers, reads one
  // attribute, and can never block a click.
  const onClick = (event) => {
    const target = event.target
    const section = typeof target?.closest === 'function' ? target.closest('[data-section]') : null
    tracker.recordClick(section?.dataset.section || '')
  }
  document.addEventListener('click', onClick, { capture: true, passive: true })
  // Registered before the leave listeners, so on a hide the timers stop first.
  const onVisibility = () => tracker.setPageVisible(document.visibilityState !== 'hidden')
  document.addEventListener('visibilitychange', onVisibility)
  onVisibility()
  const stopLeave = sendLeaveOnExit(tracker)
  activeTracker = tracker
  tracker.stop = () => {
    if (activeTracker === tracker) activeTracker = null
    stopLeave()
    document.removeEventListener('visibilitychange', onVisibility)
    document.removeEventListener('click', onClick, { capture: true })
  }
  return tracker
}
